const readAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

const estimatedByteSize = (dataUrl) => Math.ceil(((dataUrl.split(',')[1] || '').length * 3) / 4)

/**
 * Downscales an image file to the configured bounds and re-encodes it as a
 * data-URL. Returns null when the file type is not accepted or the result
 * still exceeds maxFileSize. With the `source` format, non-GIF images retain
 * their source MIME type but are still processed through canvas. JPEG and
 * WebP use the configured quality; browsers ignore it for lossless PNG output.
 * GIFs are never re-encoded (animation would get lost) - they only pass the
 * size check.
 */
export default async function downscaleImage(file, settings) {
  const { maxWidth, maxHeight, quality, format, maxFileSize, acceptedMimeTypes } = settings

  if (!acceptedMimeTypes.includes(file.type)) {
    return null
  }

  if (file.type === 'image/gif') {
    if (file.size > maxFileSize) {
      return null
    }

    return await readAsDataURL(file)
  }

  const targetFormat = format === 'source' ? file.type : format

  let bitmap

  try {
    bitmap = await createImageBitmap(file)
  } catch {
    return null
  }

  const scale = Math.min(1, maxWidth / bitmap.width, maxHeight / bitmap.height)

  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(bitmap.width * scale))
  canvas.height = Math.max(1, Math.round(bitmap.height * scale))

  const context = canvas.getContext('2d')

  if (targetFormat === 'image/jpeg') {
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvas.width, canvas.height)
  }

  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()

  const dataUrl = canvas.toDataURL(targetFormat, quality)

  if (estimatedByteSize(dataUrl) > maxFileSize) {
    return null
  }

  return dataUrl
}
